import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Zap, Clock, Play, Plus, Pencil, X } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabService, type Workflow, type WorkflowHistoryEntry, type WorkflowTemplate } from '../services/tabs';
import { toast } from 'sonner';

export function TabWorkflowAutomation() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [history, setHistory] = useState<Record<string, WorkflowHistoryEntry[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState({ name: '', description: '', trigger: 'Manual', actions: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', trigger: '', actions: '' });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [wf, tpl] = await Promise.all([tabService.getWorkflows(), tabService.getWorkflowTemplates()]);
      setWorkflows(wf);
      setTemplates(tpl);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load workflow automation data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const activeCount = useMemo(() => workflows.filter((w) => w.status === 'active').length, [workflows]);
  const totalRuns = useMemo(() => workflows.reduce((sum, w) => sum + (w.runs || 0), 0), [workflows]);

  const handleCreate = async () => {
    if (!creating.name.trim()) {
      toast.error('Workflow name is required');
      return;
    }
    try {
      await tabService.createWorkflow({
        name: creating.name.trim(),
        description: creating.description.trim(),
        trigger: creating.trigger.trim(),
        actions: creating.actions.split(',').map((x) => x.trim()).filter(Boolean),
      });
      setCreating({ name: '', description: '', trigger: 'Manual', actions: '' });
      toast.success('Workflow created');
      await loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create workflow');
    }
  };

  const toggleWorkflow = async (workflow: Workflow) => {
    if (workflow.status === 'archived') {
      toast.error('Cannot toggle an archived workflow. Restore it first.');
      return;
    }
    const next = workflow.status === 'active' ? 'paused' : 'active';
    try {
      await tabService.updateWorkflowStatus(workflow.id, next);
      toast.success(`Workflow ${next}`);
      await loadData();
    } catch {
      toast.error('Failed to update workflow status');
    }
  };

  const executeWorkflow = async (id: string) => {
    try {
      await tabService.executeWorkflow(id);
      const rows = await tabService.getWorkflowHistory(id);
      setHistory((prev) => ({ ...prev, [id]: rows }));
      toast.success('Workflow executed');
      await loadData();
    } catch {
      toast.error('Failed to execute workflow');
    }
  };

  const deleteWorkflow = async (id: string) => {
    try {
      await tabService.deleteWorkflow(id);
      toast.success('Workflow permanently deleted');
      await loadData();
    } catch {
      toast.error('Failed to delete workflow');
    }
  };

  const applyTemplate = async (id: string) => {
    try {
      await tabService.applyWorkflowTemplate(id);
      toast.success('Template applied');
      await loadData();
    } catch {
      toast.error('Failed to apply template');
    }
  };

  const startEdit = (workflow: Workflow) => {
    setEditingId(workflow.id);
    setEditForm({
      name: workflow.name,
      description: workflow.description,
      trigger: workflow.trigger,
      actions: (workflow.actions || []).join(', '),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', description: '', trigger: '', actions: '' });
  };

  const saveEdit = async (workflow: Workflow) => {
    try {
      await tabService.updateWorkflow(workflow.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        trigger: editForm.trigger.trim(),
        actions: editForm.actions.split(',').map((x) => x.trim()).filter(Boolean),
      });
      toast.success('Workflow updated');
      setEditingId(null);
      await loadData();
    } catch {
      toast.error('Failed to update workflow');
    }
  };

  const archiveRule = async (workflowId: string) => {
    try {
      await tabService.updateWorkflowStatus(workflowId, 'archived');
      toast.success('Workflow archived');
      await loadData();
    } catch {
      toast.error('Failed to archive workflow');
    }
  };

  return (
    <PageLayout title="Tab Workflow Automation" description="Manage execution, templates, history, and smart action rules">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-6"><p className="text-sm text-khiimori-text-muted">Active Workflows</p><p className="text-3xl font-bold text-khiimori-text-primary">{activeCount}</p></Card>
        <Card className="p-6"><p className="text-sm text-khiimori-text-muted">Total Runs</p><p className="text-3xl font-bold text-khiimori-text-primary">{totalRuns}</p></Card>
        <Card className="p-6"><p className="text-sm text-khiimori-text-muted">Templates</p><p className="text-3xl font-bold text-khiimori-text-primary">{templates.length}</p></Card>
        <Card className="p-6"><p className="text-sm text-khiimori-text-muted">Automation</p><div className="flex items-center gap-2 mt-2 text-primary-400"><Zap className="w-5 h-5" /> Connected</div></Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-khiimori-text-primary mb-4">Create Workflow</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10" value={creating.name} onChange={(e) => setCreating((p) => ({ ...p, name: e.target.value }))} placeholder="Workflow name" />
          <input className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10" value={creating.trigger} onChange={(e) => setCreating((p) => ({ ...p, trigger: e.target.value }))} placeholder="Trigger" />
          <input className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10" value={creating.description} onChange={(e) => setCreating((p) => ({ ...p, description: e.target.value }))} placeholder="Description" />
          <input className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10" value={creating.actions} onChange={(e) => setCreating((p) => ({ ...p, actions: e.target.value }))} placeholder="Actions (comma-separated)" />
        </div>
        <button onClick={handleCreate} className="mt-4 px-4 py-2 rounded-lg bg-primary-500 text-white inline-flex items-center gap-2"><Plus className="w-4 h-4" />Create</button>
      </Card>

      {isLoading ? <div className="py-10 text-center text-khiimori-text-muted">Loading...</div> : (
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <Card key={workflow.id} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {editingId === workflow.id ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Pencil className="w-4 h-4 text-blue-400" />
                        <span className="text-xs text-blue-400">Editing</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary"
                          value={editForm.name}
                          onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                          placeholder="Workflow name"
                        />
                        <input
                          className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary"
                          value={editForm.trigger}
                          onChange={(e) => setEditForm((p) => ({ ...p, trigger: e.target.value }))}
                          placeholder="Trigger"
                        />
                      </div>
                      <input
                        className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary w-full"
                        value={editForm.description}
                        onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Description"
                      />
                      <input
                        className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary w-full"
                        value={editForm.actions}
                        onChange={(e) => setEditForm((p) => ({ ...p, actions: e.target.value }))}
                        placeholder="Actions (comma-separated)"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-khiimori-text-primary">{workflow.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs ${workflow.status === 'active' ? 'bg-green-500/20 text-green-400' : workflow.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-khiimori-bg-elevated/20 text-khiimori-text-muted'}`}>{workflow.status}</span>
                      </div>
                      <p className="text-khiimori-text-secondary text-sm mb-2">{workflow.description}</p>
                      <div className="text-xs text-khiimori-text-muted flex items-center gap-4">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{workflow.trigger || 'Manual'}</span>
                        <span>{workflow.runs || 0} runs</span>
                        {workflow.lastRun ? <span>Last run: {workflow.lastRun}</span> : null}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(workflow.actions || []).map((action) => (
                          <span key={action} className="px-2 py-1 rounded bg-khiimori-bg-elevated/10 text-xs">{action}</span>
                        ))}
                      </div>
                    </>
                  )}
                  {(history[workflow.id] || []).length > 0 && editingId !== workflow.id ? (
                    <div className="mt-3 text-xs text-khiimori-text-muted space-y-1">
                      {(history[workflow.id] || []).slice(0, 3).map((entry) => (
                        <div key={entry.id}>- {entry.status}: {entry.details}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-2">
                  {editingId === workflow.id ? (
                    <>
                      <button onClick={() => saveEdit(workflow)} className="px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400">Save</button>
                      <button onClick={cancelEdit} className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-muted inline-flex items-center gap-1"><X className="w-4 h-4" />Cancel</button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleWorkflow(workflow)}
                        disabled={workflow.status === 'archived'}
                        className={`px-3 py-2 rounded-lg ${workflow.status === 'active' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'} ${workflow.status === 'archived' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {workflow.status === 'active' ? 'Pause' : workflow.status === 'paused' ? 'Activate' : 'Archived'}
                      </button>
                      <button
                        onClick={() => executeWorkflow(workflow.id)}
                        disabled={workflow.status === 'archived'}
                        className={`px-3 py-2 rounded-lg bg-green-500/20 text-green-400 inline-flex items-center gap-1 ${workflow.status === 'archived' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Play className="w-4 h-4" />Execute
                      </button>
                      <button onClick={() => startEdit(workflow)} className="px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400">Update Rule</button>
                      <button
                        onClick={() => archiveRule(workflow.id)}
                        disabled={workflow.status === 'archived'}
                        className={`px-3 py-2 rounded-lg bg-orange-500/20 text-orange-400 ${workflow.status === 'archived' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Delete Rule
                      </button>
                      <button onClick={() => deleteWorkflow(workflow.id)} className="px-3 py-2 rounded-lg bg-red-500/20 text-red-400">Delete</button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-khiimori-text-primary mb-4">Workflow Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {templates.map((template) => (
            <div key={template.id} className="p-4 rounded-lg border border-khiimori-border/20">
              <div className="font-semibold text-khiimori-text-primary">{template.name}</div>
              <div className="text-sm text-khiimori-text-muted mt-1">{template.description}</div>
              <button onClick={() => applyTemplate(template.id)} className="mt-3 px-3 py-1 rounded bg-primary-500/20 text-primary-400">Apply Template</button>
            </div>
          ))}
        </div>
      </Card>
    </PageLayout>
  );
}